import { RemoteResultActionPayloadByRemoteResultActionType } from '../redux/remoteResult';
import {
    MediumType,
    RemoteResult,
    RemoteResultConfiguration,
    RemoteResultConfigurationMeterRate,
    RemoteResultMeterRate,
} from '../screens/common/RemoteResultCommand/index.types';
import { splitJoin } from './TextHelper';
import { Display, ImageEvaluationResponseDTO, ImplicitMeter, MeterDevice, Rate, RelBox } from '../api/ai.api.types';
import MeterHelper, { METER_TYPES } from './MeterHelper';
import { OutcomeType } from '../screens/common/RemoteResultCommand/remoteResultOutcome';

const remoteResultObisRegex = /(\d)-(\d{1,2})[:](\d{1,2})\.(\d{1,2})\.(\d{1,2})/;

const resolveMeterMediumByObis = (obis: string | undefined): MediumType => {
    const match = obis?.match(remoteResultObisRegex);

    switch (Number(match ? match[1] : 0)) {
        case 1:
            return 'ELECTRICITY';
        case 6:
            return 'HEAT';
        case 7:
            return 'GAS';
        case 8:
            return 'WATER';
        case 9:
            return 'WATER_WARM';
        default:
            break;
    }

    return 'ABSTRACT';
};

const resolveMeterTypeByMedium = (medium: MediumType | undefined): METER_TYPES => {
    if (!medium) {
        return METER_TYPES.GENERIC_METER;
    }

    switch (medium) {
        case 'ELECTRICITY': {
            return METER_TYPES.ELECTRIC_METER;
        }
        case 'HEAT': {
            return METER_TYPES.HEAT_METER;
        }
        case 'GAS': {
            return METER_TYPES.GAS_METER;
        }
        case 'WATER': {
            return METER_TYPES.WATER_METER;
        }
        case 'WATER_WARM': {
            return METER_TYPES.WATER_WARM_METER;
        }
        default: {
            break;
        }
    }

    return METER_TYPES.GENERIC_METER;
};

// Boxes that (nearly) cover the whole image are placeholders ("somewhere in this picture"), not
// localizations — e.g. an ImplicitMeter inferred from a visible display carries a 0/0/1/1 box.
// Passing them through would anchor the live tracker and overlay on the entire screen.
const FULL_IMAGE_AREA_THRESHOLD = 0.95;
const localizedBox = (box: RelBox | undefined): RelBox | undefined => {
    if (!box) {
        return undefined;
    }
    return box.w * box.h >= FULL_IMAGE_AREA_THRESHOLD ? undefined : box;
};

const mapImageEvaluationResponseToRemoteResult = (
    response: ImageEvaluationResponseDTO | undefined,
    options?: { allowEmptyRates?: boolean },
): RemoteResult | undefined => {
    if (response == null) {
        return undefined;
    }

    const meterDevice = response.devices?.find((d): d is ImplicitMeter | MeterDevice => 'inferredMeter' in d);

    if (!meterDevice) {
        return undefined;
    }

    const inferredMeter = meterDevice.inferredMeter;

    if (!inferredMeter) {
        return undefined;
    }

    const rates = inferredMeter.rates ?? [];

    const mapRate = (r: Rate): RemoteResultMeterRate => ({
        obis: r.obis ?? undefined,
        obisConfidence: r.obisConfidence ?? 0,
        obisRateName: r.obisRateName ?? undefined,
        readingValue: r.value ?? undefined,
        readingValueConfidence: r.valueConfidence ?? 0,
        valueBox: r.valueBox ?? undefined,
        displayBox: r.displayBox ?? undefined,
    });

    let meterRates: Array<RemoteResultMeterRate> = [];

    // Exactly one detected rate
    if (rates.length === 1) {
        meterRates = [mapRate(rates[0])];
    }
    // Multiple detected rates
    else if (rates.length > 1) {
        const withObis = rates.filter((r) => r.obis != null);

        // If some have OBIS → keep existing best-by-confidence selection (single rate)
        if (withObis.length > 0) {
            const best = withObis.reduce((a, b) => {
                const av = a.valueConfidence ?? -Infinity;
                const bv = b.valueConfidence ?? -Infinity;

                if (bv !== av) return bv > av ? b : a;

                const ao = a.obisConfidence ?? -Infinity;
                const bo = b.obisConfidence ?? -Infinity;

                return bo > ao ? b : a;
            });

            meterRates = [mapRate(best)];
        }
        // None had OBIS → pass through every rate that carries a tariff label (obisRateName),
        // so an analog dual-tariff reading (HT/NT) can be matched per tariff downstream.
        else {
            meterRates = rates.filter((r) => r.obisRateName != null && r.obisRateName !== '').map(mapRate);
        }
    }

    // Without rates there is nothing to evaluate for the single-shot flows, so they keep getting
    // `undefined` — UNLESS the device itself was recognized (medium and/or id). A recognized
    // device with unmappable rates (e.g. an analog dual-tariff reading without obisRateName) is
    // still useful signal: it lets resolveRemoteResultOutcome report a concrete INVALID_MEDIUM /
    // INVALID_METER_NUMBER instead of treating "tariffs unclear" the same as "nothing recognized
    // at all". The live assistant passes `allowEmptyRates` for the same reason in its own flow —
    // a meter/display box alone is already useful there (framing feedback + overlay), even when
    // no value is readable yet.
    if (meterRates.length === 0 && !options?.allowEmptyRates) {
        if (inferredMeter.medium == null && inferredMeter.id == null) {
            return undefined;
        }
    }

    const primaryRate: RemoteResultMeterRate | undefined = meterRates[0];

    return {
        medium: inferredMeter.medium,
        meterNumber: inferredMeter.id ?? undefined,
        meterNumberConfidence: inferredMeter.idConfidence ?? 0,
        meterNumberCandidates: (inferredMeter.otherIdCandidates ?? []).map((c) => ({ id: c.id, confidence: c.confidence ?? 0 })),
        meterRates: meterRates,
        detectedMeterBox: localizedBox(meterDevice.box),
        detectedDisplayBox: localizedBox(meterDevice.children?.find((c): c is Display => c.type === 'Display')?.box),
        detectedValueBox: primaryRate?.valueBox,
        meta: {
            expectedMeterNumber: undefined,
            detectedMeterNumber: inferredMeter.id,
            expectedObis: undefined,
            detectedObis: primaryRate?.obis,
            expectedReadingValue: undefined,
            detectedReadingValue: primaryRate?.readingValue,
            expectedMedium: undefined,
            detectedMedium: inferredMeter.medium,
            warnings: undefined,
        },
    };
};

// Meter-number mismatches are only treated as "surely wrong" (INVALID_METER_NUMBER) at or above
// this confidence — otherwise we're not sure enough about the detected number itself to actively
// reject, and fall back to a manual NO_RESULT instead. This matters most when no tariff row could
// be read/mapped (see below): identity is then the only signal we have, so it must be trustworthy
// before we use it to hard-reject.
const METER_NUMBER_MISMATCH_CONFIDENCE_THRESHOLD = 0.8;

const resolveRemoteResultOutcome = (result: RemoteResult | undefined, config: RemoteResultConfiguration): OutcomeType => {
    const resultRate = result?.meterRates?.[0];
    const configRate = config?.sourceMeterRate;

    // Medium/meter-number identity checks run BEFORE the "no tariff row" short-circuit below, so
    // a recognized-but-unmappable device (e.g. rates without obis/obisRateName — see
    // mapImageEvaluationResponseToRemoteResult) can still be flagged as a confident mismatch
    // instead of falling through to a generic NO_RESULT.

    // Medium mismatch is a robust "surely wrong" signal on its own — only fires when both sides
    // are known.
    const resultMedium = result?.medium ?? null;
    const configMedium = configRate?.obis ? resolveMeterMediumByObis(configRate.obis) : null;

    if (resultMedium !== null && configMedium !== null && resultMedium !== configMedium) {
        return 'INVALID_MEDIUM';
    }

    const resultMeterNumber = result?.meterNumber ?? null;
    const configMeterNumber = config?.meterNumber ?? null;

    // Both values must be present for meter number validation to trigger.
    // If the AI did not recognise the primary meter number (resultMeterNumber is null),
    // validation is skipped and the result is non-blocking (falls through to VALID/NO_RESULT).
    if (resultMeterNumber && configMeterNumber) {
        const resultMatch = extractSmartMeterNumber(resultMeterNumber);
        const configMatch = extractSmartMeterNumber(configMeterNumber);

        const matched = isMeterNumbersMatched(configMatch, resultMatch, config.meterNumberMatchTrailingCharsCount);

        if (!matched && (result?.meterNumberConfidence ?? 0) >= METER_NUMBER_MISMATCH_CONFIDENCE_THRESHOLD) {
            return 'INVALID_METER_NUMBER';
        }
    }

    if (!resultRate) {
        // No tariff row could be read/mapped (e.g. rates detected without obis/obisRateName),
        // and identity above did not produce a confident mismatch → manual review, not a hard
        // reject.
        return 'NO_RESULT';
    }

    const resultObis = resultRate?.obis ?? null;
    const configObis = configRate?.obis ?? null;

    if (resultObis && configObis) {
        const rShort = MeterHelper.shortenObis(resultObis);
        const cShort = MeterHelper.shortenObis(configObis);

        if (rShort !== cShort) {
            return 'INVALID_OBIS';
        }
    }

    // could not invalidate, therefore valid by default
    return 'VALID';
};

export interface CropResult {
    displayCrop?: string;
    valueCrop?: string;
}

/**
 * - If no displayBox -> { displayCrop: undefined, valueCrop: undefined }
 * - If displayBox only -> { displayCrop }
 * - If displayBox + valueBox -> { displayCrop, valueCrop }
 */
// A meter display is physically always wider than tall — a portrait-shaped crop therefore means
// the meter was photographed rotated ~90° (e.g. lying on its side). Rotating clockwise puts the
// bottom edge on the left, so a reading running bottom-to-top reads left-to-right again.
const rotateDataUrl90CW = async (dataUrl: string, mime: string): Promise<string> => {
    const img = await loadImage(dataUrl);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
    const ctx = canvas.getContext('2d')!;
    ctx.translate(canvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL(mime);
};

const toLandscape = (crop: string, abs: { w: number; h: number }, mime: string): Promise<string> | string =>
    abs.h > abs.w ? rotateDataUrl90CW(crop, mime) : crop;

export const cropImage = async (imageBase64: string, displayBox?: RelBox, valueBox?: RelBox): Promise<CropResult> => {
    if (!displayBox) return { displayCrop: undefined, valueCrop: undefined };

    const mime = getMimeFromDataUrl(imageBase64) ?? 'image/png';
    const img = await loadImage(imageBase64);

    // 1) Display crop — the unrotated crop is kept: valueBox below is relative to it
    const absDisplay = toAbsoluteBox(img.naturalWidth, img.naturalHeight, displayBox);
    const rawDisplayCrop = cropToDataUrl(img, absDisplay, mime);
    const displayCrop = await toLandscape(rawDisplayCrop, absDisplay, mime);

    if (!valueBox) return { displayCrop };

    // 2) Value crop (from the unrotated display crop)
    const displayImg = await loadImage(rawDisplayCrop);
    const absValue = toAbsoluteBox(displayImg.naturalWidth, displayImg.naturalHeight, valueBox);
    const valueCrop = await toLandscape(cropToDataUrl(displayImg, absValue, mime), absValue, mime);

    return { displayCrop, valueCrop };
};

// Enriches a result with display crops: the top-level crop (kept for the dialog preview) and,
// when a rate carries its own displayBox (e.g. analog HT/NT meters), a per-tariff crop on each
// rate so every tariff task can show its own display snippet instead of one shared crop.
const enrichResultWithDisplayCrops = async (result: RemoteResult | undefined, imageBase64: string): Promise<RemoteResult | undefined> => {
    if (!result) {
        return result;
    }

    const topLevelCrop = await cropImage(imageBase64, result.detectedDisplayBox, result.detectedValueBox);

    const meterRates = await Promise.all(
        result.meterRates.map(async (mr) => {
            if (!mr.displayBox) {
                return mr;
            }
            const { displayCrop } = await cropImage(imageBase64, mr.displayBox, mr.valueBox);
            return { ...mr, detectedDisplayCropBase64: displayCrop };
        }),
    );

    return { ...result, detectedDisplayCropBase64: topLevelCrop?.displayCrop, meterRates };
};

const getMimeFromDataUrl = (dataUrl: string): string | null => {
    const m = /^data:([^;,]+)[;,]/.exec(dataUrl);
    return m ? m[1] : null;
};

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

const sanitizeRelBox = (b: RelBox): RelBox => {
    const x = clamp01(b.x);
    const y = clamp01(b.y);
    const w = clamp01(b.w);
    const h = clamp01(b.h);

    return {
        x,
        y,
        w: Math.max(0, Math.min(w, 1 - x)),
        h: Math.max(0, Math.min(h, 1 - y)),
    };
};

const toAbsoluteBox = (imgW: number, imgH: number, rel: RelBox) => {
    const r = sanitizeRelBox(rel);
    const x = Math.round(r.x * imgW);
    const y = Math.round(r.y * imgH);
    const w = Math.max(1, Math.round(r.w * imgW));
    const h = Math.max(1, Math.round(r.h * imgH));
    return { x, y, w, h };
};

const cropToDataUrl = (
    source: CanvasImageSource,
    abs: {
        x: number;
        y: number;
        w: number;
        h: number;
    },
    mime: string,
): string => {
    const canvas = document.createElement('canvas');
    canvas.width = abs.w;
    canvas.height = abs.h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(source, abs.x, abs.y, abs.w, abs.h, 0, 0, abs.w, abs.h);
    return canvas.toDataURL(mime);
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to decode base64 image'));
        img.src = dataUrl;
    });

const normalizeRateName = (rateName: string | undefined | null): string => rateName?.trim().toUpperCase() ?? '';

// Tariff label match (e.g. "HT"/"NT"). Used as the OBIS fallback for analog dual-tariff
// meters: a match only counts when BOTH the master-data rateName and the AI obisRateName are
// set and equal (trimmed, case-insensitive).
const isRateNameMatched = (rateName: string | undefined | null, obisRateName: string | undefined | null): boolean => {
    const a = normalizeRateName(rateName);
    const b = normalizeRateName(obisRateName);
    return a !== '' && b !== '' && a === b;
};

// Resolves which meter-rate task a detected AI rate belongs to.
// Priority: (1) tariff label (obisRateName ↔ rateName) when the rate has no OBIS,
// (2) OBIS match, (3) sourceTaskId fallback — only when explicitly allowed (e.g. a single
// detected rate), so a multi-tariff reading is never collapsed onto one task.
const findMeterRateTaskForRate = <T extends { obis?: string; rateName?: string; taskId?: string }>(
    rate: { obis?: string; obisRateName?: string },
    tasks: Array<T>,
    options?: { sourceTaskId?: string; allowSourceTaskIdFallback?: boolean },
): T | undefined => {
    if (!rate.obis && rate.obisRateName) {
        const byRateName = tasks.find((t) => isRateNameMatched(t.rateName, rate.obisRateName));
        if (byRateName) {
            return byRateName;
        }
    }

    if (rate.obis) {
        const shortObis = MeterHelper.shortenObis(rate.obis);
        const byObis = tasks.find((t) => MeterHelper.shortenObis(t.obis) === shortObis);
        if (byObis) {
            return byObis;
        }
    }

    if (options?.allowSourceTaskIdFallback && options.sourceTaskId) {
        const bySourceTaskId = tasks.find((t) => t.taskId === options.sourceTaskId);
        if (bySourceTaskId) {
            return bySourceTaskId;
        }
    }

    return undefined;
};

// Inverse of findMeterRateTaskForRate: given a tariff task, finds the detected rate that
// belongs to it. Used to pick the per-tariff display crop for display next to each task.
// Priority mirrors the assignment logic: tariff label (obisRateName ↔ rateName) for rates
// without OBIS, then OBIS match.
const findRateForMeterRateTask = <R extends { obis?: string; obisRateName?: string }>(
    task: { obis?: string; rateName?: string },
    rates: Array<R>,
): R | undefined => {
    const byRateName = rates.find((r) => !r.obis && isRateNameMatched(task.rateName, r.obisRateName));
    if (byRateName) {
        return byRateName;
    }

    if (task.obis) {
        const shortObis = MeterHelper.shortenObis(task.obis);
        const byObis = rates.find((r) => MeterHelper.shortenObis(r.obis) === shortObis);
        if (byObis) {
            return byObis;
        }
    }

    return undefined;
};

const findMeterRateConfiguration = (
    obis: string | undefined,
    obisRateName: string | undefined,
    meterRateConfigurations: Array<RemoteResultConfigurationMeterRate>,
): RemoteResultConfigurationMeterRate | undefined => {
    if (!meterRateConfigurations.length) {
        return undefined;
    }

    const shortObis = MeterHelper.shortenObis(obis);

    // No OBIS but a tariff label → match by the configured rateName so each tariff gets its own config.
    if (!shortObis && obisRateName) {
        const byRateName = meterRateConfigurations.find((c) => isRateNameMatched(c.rateName, obisRateName));
        if (byRateName) {
            return byRateName;
        }
    }

    if (!shortObis) {
        return meterRateConfigurations?.[0];
    }

    const matchedMeterRate = meterRateConfigurations.find((c) => MeterHelper.shortenObis(c.obis) === shortObis);
    if (matchedMeterRate) {
        return matchedMeterRate;
    }

    return meterRateConfigurations?.[0];
};

const mapRemoteResultToPayload = (
    result: RemoteResult,
    images: Array<string> | undefined,
    config: RemoteResultConfiguration,
): RemoteResultActionPayloadByRemoteResultActionType['REMOTE_RESULT_RECEIVED'] => {
    return {
        ...result,
        meterRates: result.meterRates.map((rmr) => {
            const meterRateConfigurations: Array<RemoteResultConfigurationMeterRate> = [];

            if (config.sourceMeterRate) {
                meterRateConfigurations.push(config.sourceMeterRate);
            }

            if (config.otherMeterRates?.length) {
                config.otherMeterRates.forEach((omr) => {
                    meterRateConfigurations.push(omr);
                });
            }

            const meterRateConfig = findMeterRateConfiguration(rmr.obis, rmr.obisRateName, meterRateConfigurations);

            const enrichedReadingValue = enrichRemoteResultReadingValue(
                rmr.readingValue,
                meterRateConfig?.preDecimals,
                meterRateConfig?.postDecimals,
                config?.padReadingValuePostDecimalsEnabled,
            );

            return {
                ...rmr,
                readingValue: enrichedReadingValue,
                readingImages: images,
            };
        }),
    };
};

const enrichRemoteResultReadingValue = (
    readingValue: string | undefined,
    preDecimals: number = 8,
    postDecimals: number = 0,
    padPostDecimalsEnabled: boolean = false,
) => {
    if (readingValue == null) {
        return undefined;
    }

    let [newPreDecimals, newPostDecimals = ''] = readingValue.split('.');
    const newPostDecimalsCount = newPostDecimals?.length || 0;
    const postDecimalsMatched = postDecimals === newPostDecimalsCount;

    if (newPreDecimals === '') {
        newPreDecimals = '0';
    }

    if (!postDecimalsMatched) {
        if (postDecimals === 0) {
            readingValue = newPreDecimals;
        } else if (padPostDecimalsEnabled && newPostDecimalsCount < postDecimals) {
            newPostDecimals = newPostDecimals.padEnd(postDecimals, '0');
            readingValue = splitJoin('.', newPreDecimals, newPostDecimals)!;
        } else {
            newPostDecimals = newPostDecimals.slice(0, postDecimals);
            readingValue = splitJoin('.', newPreDecimals, newPostDecimals)!;
        }
    }

    readingValue = readingValue.replace(/^0+(?=\d)/, ''); // remove leading zeros from integers
    if (readingValue.startsWith('.')) {
        readingValue = '0' + readingValue; // add leading zero if number starts with a dot
    }

    const preDecimalsLength = readingValue.split('.')[0].length;

    if (preDecimalsLength > preDecimals) {
        return undefined;
    }

    return readingValue;
};

// Default number of trailing characters that must match when accepting an
// otherIdCandidates value and no explicit meterNumberMatchTrailingCharsCount is configured.
const DEFAULT_CANDIDATE_MATCH_TRAILING_CHARS_COUNT = 6;

const SMART_METER_REGEX = /\d[A-Z]{3}\d{11}/;

// Extracts the comparable part of a meter number. For smart meter numbers we
// compare the canonical embedded segment (e.g. "1ENV0123456789"); otherwise the
// value is compared as-is.
const extractSmartMeterNumber = (s: string): string => {
    const m = s.match(SMART_METER_REGEX);
    return m ? m[0] : s;
};

// Examines otherIdCandidates when the primary meter number does not match or was
// not recognized. Only fires when config.meterNumber is set — without a comparison
// value a candidate cannot be validated as a meter number.
//   1. id matches expected → no change
//   2. id doesn't match (or is absent), candidate matches expected → meterNumber = expected,
//      meta.detectedMeterNumber updated to the matched candidate id
//   3. no candidate matches, or no expected configured → result unchanged
const applyMeterNumberCandidateMatch = (result: RemoteResult | undefined, config: RemoteResultConfiguration): RemoteResult | undefined => {
    if (!result) {
        return result;
    }

    const expected = config?.meterNumber;
    const detected = result.meterNumber;
    const candidates = (result.meterNumberCandidates ?? []).filter((c) => c?.id);

    if (!expected || candidates.length === 0) {
        return result;
    }

    const expectedMatch = extractSmartMeterNumber(expected);
    // count is passed as-is to the primary match (null disables trailing-chars check there).
    // candidateCount always defaults to 6 so candidates are still matched by trailing chars
    // even when the primary check has no trailing-chars constraint configured.
    const count = config?.meterNumberMatchTrailingCharsCount;
    const candidateCount = count ?? DEFAULT_CANDIDATE_MATCH_TRAILING_CHARS_COUNT;

    // Primary number already matches → nothing to do.
    if (detected && isMeterNumbersMatched(expectedMatch, extractSmartMeterNumber(detected), count)) {
        return result;
    }

    // Find the first candidate whose id matches the expected number.
    // Note: `confidence` from each candidate is preserved in the type but not used for ranking here —
    // candidates are returned by the AI in relevance order and the first match wins.
    // If confidence-based ranking becomes desirable, replace find() with a sort-then-find.
    const matchingCandidate = candidates.find((c) => isMeterNumbersMatched(expectedMatch, extractSmartMeterNumber(c.id), candidateCount));

    if (matchingCandidate) {
        return {
            ...result,
            meterNumber: expected,
            meta: { ...result.meta, detectedMeterNumber: matchingCandidate.id },
        };
    }

    return result;
};

const isMeterNumbersMatched = (expected: string | undefined, detected: string | undefined, meterNumberMatchTrailingCharsCount?: number | null): boolean => {
    const safeExpected = expected?.trim()?.toUpperCase();
    const safeDetected = detected?.trim()?.toUpperCase();

    const expectedValid = safeExpected != null && safeExpected !== '';
    const detectedValid = safeDetected != null && safeDetected !== '';

    // meterNumberMatchTrailingCharsCount explicit 0 means, we skip validation
    if (!expectedValid || !detectedValid || meterNumberMatchTrailingCharsCount === 0) {
        return true;
    }

    const fullyMatched = safeExpected === safeDetected;
    if (fullyMatched) {
        return true;
    }

    const partiallyMatched = safeDetected.endsWith(safeExpected);
    if (partiallyMatched) {
        return true;
    }

    if (meterNumberMatchTrailingCharsCount != null && meterNumberMatchTrailingCharsCount >= 1) {
        const trailingCharsCountMatched = safeExpected.slice(-meterNumberMatchTrailingCharsCount) === safeDetected.slice(-meterNumberMatchTrailingCharsCount);
        if (trailingCharsCountMatched) {
            return true;
        }
    }

    return false;
};

// Fills the expected OBIS in on detected rates that carry none — the AI frequently reads a value
// without being able to read its OBIS, and the confirm step knows which register was photographed.
// Only ever applied to a SINGLE detected rate: for a multi-tariff reading (analog HT/NT, told apart
// solely by obisRateName) stamping the source register's OBIS onto every rate makes all of them
// match that one task by OBIS — which beats the obisRateName match and collapses every tariff value
// onto the source register, so the last rate wins and the other register stays empty.
const applyExpectedObisToRates = (result: RemoteResult, expectedObis: string | undefined): RemoteResult => {
    const meterRates = result.meterRates ?? [];

    if (meterRates.length !== 1) {
        return result;
    }

    return { ...result, meterRates: meterRates.map((mr) => ({ ...mr, obis: mr.obis || expectedObis })) };
};

const buildForcedResult = (result: RemoteResult | undefined, config: RemoteResultConfiguration, obis: string | undefined): RemoteResult => {
    const meterRates = result?.meterRates || [];

    // Force the expected OBIS only onto a single detected rate (single-tariff confirm).
    // For a multi-tariff reading (e.g. analog HT/NT without OBIS) overwriting every rate
    // with the same OBIS would collapse all values onto one task — keep each rate's
    // obisRateName so the values stay assignable per tariff.
    const forcedMeterRates = meterRates.length === 1 ? meterRates.map((mr) => ({ ...mr, obis })) : meterRates;

    return {
        ...result,
        meterNumber: config.meterNumber,
        meterNumberConfidence: 1,
        meterRates: forcedMeterRates,
        meta: result?.meta as RemoteResult['meta'],
    };
};

// Whether at least one detected rate can be assigned to a meter rate task — the same matching the
// REMOTE_RESULT_RECEIVED handler in JobDetails applies (source task fallback only for a single rate).
// If not, dispatching the result would set nothing, not even the photo (e.g. "take photo" after the
// AI read no rate or the rates of a meter of another medium).
const canAssignResultToMeterRateTasks = <T extends { obis?: string; rateName?: string; taskId?: string }>(
    result: RemoteResult,
    tasks: Array<T>,
    sourceTaskId: string | undefined,
): boolean => {
    const meterRates = result.meterRates ?? [];
    const allowSourceTaskIdFallback = meterRates.length <= 1;

    return meterRates.some(
        (mr) => findMeterRateTaskForRate({ obis: mr.obis, obisRateName: mr.obisRateName }, tasks, { sourceTaskId, allowSourceTaskIdFallback }) != null,
    );
};

export default {
    resolveMeterMediumByObis,
    resolveMeterTypeByMedium,
    mapRemoteResultToPayload,
    mapImageEvaluationResponseToRemoteResult,
    resolveRemoteResultOutcome,
    cropImage,
    enrichResultWithDisplayCrops,
    enrichRemoteResultReadingValue,
    isMeterNumbersMatched,
    isRateNameMatched,
    findMeterRateTaskForRate,
    findRateForMeterRateTask,
    applyMeterNumberCandidateMatch,
    applyExpectedObisToRates,
    buildForcedResult,
    canAssignResultToMeterRateTasks,
} as const;
